import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { formatNumberToCurrency } from '@/utils/currency';
import advisorWidgetMap, {
  AdvisorWidgetCodes,
} from '@/widget-mappings/AdvisorWidgetMap';
import WidgetLoader from '@/widget-mappings/widgetLoader';
import useClientActionsMenu, {
  ClientActionKey,
} from '@/hooks/useClientActionsMenu';
import ActionsMenuCell from '@/components/common/ActionsMenuCell';
import {
  DEFAULT_CLIENT_DETAIL_TAB,
  isClientDetailTabKey,
} from '@/modules/clientServices/utils/clientDetailTabs';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import {
  FLYOUT_CONTENT_PADDING_X,
  FLYOUT_CONTENT_PADDING_Y,
} from '../constants';
import { ClientContentProps } from '../types';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PortalActivity from './PortalActivity';
const Insights = React.lazy(async () => import('./Insights'));
import { useLocalization } from '@/hooks/useLocalization';
import { APP_DATE_FORMAT } from '@/constants/dateFormats';
import { ContentContainer } from '@/widgets/contentContainer';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { tabSelectDropdownStyling } from '@/constants/styles';
import { useTabVisibility } from './useTabVisibility';
import useCanInviteInvestor from '@/hooks/useCanInviteInvestor';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';

const ClientInviteModal = React.lazy(
  async () => import('@/modules/clientServices/ClientInviteModal'),
);

interface TabItem {
  key: string;
  label: string;
  visible: boolean;
  icon?: ReactElement;
}

const HIDDEN_CLIENT_ACTIONS: ReadonlyArray<ClientActionKey> = [
  'accessInvestor',
];

const ClientContent: React.FC<ClientContentProps> = ({
  client,
  onDeleted,
  tabSelection,
}) => {
  const { t } = useLocalization();

  const controlledTabKey =
    tabSelection?.mode === 'controlled' ? tabSelection.value : undefined;
  const uncontrolledInitialTabKey =
    tabSelection?.mode === 'uncontrolled'
      ? tabSelection.initialValue
      : undefined;
  const [uncontrolledTabKey, setUncontrolledTabKey] = useState(
    uncontrolledInitialTabKey ?? DEFAULT_CLIENT_DETAIL_TAB,
  );

  useEffect(() => {
    if (tabSelection?.mode === 'controlled') return;

    const nextTabKey = uncontrolledInitialTabKey ?? DEFAULT_CLIENT_DETAIL_TAB;
    setUncontrolledTabKey((previousTabKey) =>
      previousTabKey === nextTabKey ? previousTabKey : nextTabKey,
    );
  }, [client.InvestorId, tabSelection?.mode, uncontrolledInitialTabKey]);

  const selectedTabKey = controlledTabKey ?? uncontrolledTabKey;
  const selectTab = (tabKey: string) => {
    if (!isClientDetailTabKey(tabKey)) return;

    if (tabSelection?.mode === 'controlled') {
      tabSelection.onChange(tabKey);
      return;
    }

    setUncontrolledTabKey(tabKey);
  };

  const isAuthLoaded = useAuthIsLoaded();
  const { data: advisorGroupSettings, isError: didAdvisorGroupSettingsFail } =
    useGetAdvisorGroupSettingsQuery(undefined, { skip: !isAuthLoaded });
  const areAdvisorGroupSettingsResolved =
    advisorGroupSettings !== undefined || didAdvisorGroupSettingsFail;
  const showCusip = AdvisorGroupSettings(advisorGroupSettings ?? []).getByKey(
    'ShowCUSIPs',
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.up('md'));

  const MAX_TABS_VISIBLE = 6;

  const canInviteInvestor = useCanInviteInvestor();

  const {
    MenuForInvestor,
    Accompaniment,
    openMenuForInvestor,
    setViewMode,
    viewMode,
    impersonationError,
    clearImpersonationError,
  } = useClientActionsMenu({
    onDeleted,
    hideActions: HIDDEN_CLIENT_ACTIONS,
  });
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);

  const userInfo = useSelector((s: WARootState) => s.appView.UserInfo);
  const isMgpEnabled = Boolean(
    userInfo?.Integrations &&
    (userInfo.Integrations as Record<string, unknown>)['MGP'],
  );
  const handleAccessMoneyGuide = () => {
    try {
      const token = userInfo?.Token ?? '';
      const platformUrl = import.meta.env.VITE_PLATFORM_URL ?? '';
      const url =
        platformUrl +
        '/SSO/MoneyGuidePro?investorId=' +
        encodeURIComponent(String(client.InvestorId)) +
        '&token=' +
        encodeURIComponent(token);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // no-op
    }
  };
  const handleAccessInvestor = () => {
    setViewMode('accessInvestor', client);
  };

  // Stable per mount (fresh each time the flyout opens) rather than frozen at
  // module load, so the "as of" date can't go stale in a long-lived session.
  const todayIso = useMemo(() => new Date().toISOString(), []);

  // More dropdown state
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const isMoreOpen = Boolean(moreAnchorEl);
  const closeMoreMenu = () => {
    setMoreAnchorEl(null);
  };

  const openMoreMenuFromTabs = (event: React.SyntheticEvent) => {
    if (event.currentTarget instanceof HTMLElement) {
      setMoreAnchorEl(event.currentTarget);
    }
  };

  const {
    isBalanceSheetEnabled,
    isDocumentsEnabled,
    isAccountsEnabled,
    isHoldingsEnabled,
    isDiversificationEnabled,
    isInsightsEnabled,
    isGainsLossesEnabled,
    isCashFlowEnabled,
    isPortalActivityEnabled,
    isToDoEnabled,
    isInvestmentActivityEnabled,
    areTabFlagsResolved,
  } = useTabVisibility(
    client,
    advisorGroupSettings ?? [],
    areAdvisorGroupSettingsResolved,
  );

  const tabs: TabItem[] = useMemo(() => {
    /**
      Default display names, in tab order:

      Balance Sheet, Accounts, Holdings, Portfolio Diversification, Documents
      Insights (feature-flagged), Gains & Losses (feature-flagged), Trading Activity
      Cash Flow, Portal Activity, To Do
    */
    const tabs = [
      {
        key: 'balanceSheet',
        label: t('balanceSheet', 'Balance Sheet'),
        visible: isBalanceSheetEnabled,
      },
      {
        key: 'accounts',
        label: t('accounts', 'Accounts'),
        visible: isAccountsEnabled,
      },
      {
        key: 'holdings',
        label: t('holdings', 'Holdings'),
        visible: isHoldingsEnabled,
      },
      {
        key: 'assetAllocation',
        label: t('Asset Allocation'), // AKA: Portfolio Diversification, configurable
        visible: isDiversificationEnabled,
      },
      {
        key: 'documents',
        label: t('documents.title'),
        visible: isDocumentsEnabled,
      },
      {
        key: 'insights',
        label: t('insights', 'Insights'),
        visible: isInsightsEnabled,
      },
      {
        key: 'gainsLosses',
        label: t('gainsLosses', 'Gains & Losses'),
        visible: isGainsLossesEnabled,
      },
      {
        key: 'investmentActivity',
        label: t('Trading Activity'), // configurable
        visible: isInvestmentActivityEnabled,
      },
      {
        key: 'cashFlow',
        label: t('Cash Flow'), // configurable,
        visible: isCashFlowEnabled,
      },
      {
        key: 'portalActivity',
        label: t('portalActivity', 'Portal Activity'),
        visible: isPortalActivityEnabled,
      },
      {
        key: 'toDo',
        label: t('todo', 'To Do'),
        visible: isToDoEnabled,
      },
    ];

    return tabs.filter((x) => x.visible);
  }, [
    t,
    isBalanceSheetEnabled,
    isAccountsEnabled,
    isHoldingsEnabled,
    isDiversificationEnabled,
    isDocumentsEnabled,
    isInsightsEnabled,
    isGainsLossesEnabled,
    isInvestmentActivityEnabled,
    isCashFlowEnabled,
    isPortalActivityEnabled,
    isToDoEnabled,
  ]);

  /**
   * Display up to MAX_TABS_VISIBLE. If there's more tabs,
   * then they get pushed to the "More" menu.
   * Additionally sets the default to the first tab if
   * a tab wasn't preselected.
   */
  const visibleTabs = useMemo(() => {
    const visible: TabItem[] = tabs.slice(0, MAX_TABS_VISIBLE);

    if (tabs.length > visible.length) {
      visible.push({
        key: 'more',
        label: t('more', 'More'),
        visible: true,
        icon: <ArrowDropDownIcon />,
      });
    }

    return visible;
  }, [t, tabs]);

  // The tab that's actually safe to render: `selectedTabKey` when it's one of
  // the tabs enabled for this client, otherwise the first enabled tab (or
  // undefined while tabs/settings haven't resolved yet). Content below gates
  // on this, not on `selectedTabKey` directly, so a disabled/unresolved tab's
  // widget never mounts — not even for one render — while the effect below
  // syncs the external (URL/controlled) selection to match. Gated on
  // areTabFlagsResolved (both pageVisibility and advisorGroupSettings), not
  // just the settings half, so a still-loading pageVisibility query can't
  // make this pick a tab based on a fail-closed "disabled" flag that flips
  // true a moment later.
  const effectiveTabKey = useMemo(() => {
    if (!areTabFlagsResolved) return undefined;
    if (tabs.some((tab) => tab.key === selectedTabKey)) return selectedTabKey;
    return tabs.find((tab) => isClientDetailTabKey(tab.key))?.key;
  }, [tabs, selectedTabKey, areTabFlagsResolved]);

  // Redirect to the first enabled tab whenever the current selection isn't
  // one of the tabs actually enabled for this client (no preselection, an
  // explicit/URL-driven selection that's disabled for this client, or a tab
  // that became disabled after the tab flags finished resolving).
  useEffect(() => {
    if (!areTabFlagsResolved) return;
    if (tabs.length === 0) return;
    if (tabs.some((tab) => tab.key === selectedTabKey)) return;
    if (effectiveTabKey) {
      selectTab(effectiveTabKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, selectedTabKey, areTabFlagsResolved]);

  /**
   * Items that display under the "More" menu.
   * Instead of calculating tab size, we're assuming that at
   * least six items will fit in lg & up screen sizes. This
   * reduces complexity.
   */
  const moreTabs = useMemo(() => {
    return tabs.slice(MAX_TABS_VISIBLE);
  }, [tabs]);

  const selectedTabTrigger =
    effectiveTabKey && moreTabs.some((tab) => tab.key === effectiveTabKey)
      ? 'more'
      : effectiveTabKey;

  const data = useMemo(() => {
    return [
      { title: t('netWorth'), value: formatNumberToCurrency(client.NetWorth) },
      {
        title: t('grid.managedAssets'),
        value: formatNumberToCurrency(client.Home || 0),
      },
      {
        title: t('percentManagedAssets'),
        value:
          typeof client.HomePercentage === 'number'
            ? `${client.HomePercentage.toFixed(2)}%`
            : '—',
      },
      { title: t('username'), value: client.UserName },
      {
        title: t('lastLogin'),
        value: client.LastLogin
          ? dayjs(client.LastLogin).format(APP_DATE_FORMAT)
          : '—',
      },
      {
        title: t('clientGroups'),
        value:
          client.Groups && client.Groups.length
            ? client.Groups.map((group) => group.Name).join(', ')
            : '—',
      },
    ];
  }, [client, t]);

  return (
    <Box
      sx={{
        px: FLYOUT_CONTENT_PADDING_X,
        py: FLYOUT_CONTENT_PADDING_Y,
      }}
    >
      {Accompaniment ? (
        Accompaniment
      ) : (
        <Box sx={{ mb: 4 }}>
          {/* Action buttons row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 4,
            }}
          >
            {client.CanImpersonate ? (
              <Button
                variant='contained'
                onClick={handleAccessInvestor}
                disabled={viewMode === 'accessInvestor'}
              >
                {t('accessClientPortal', 'Access Client Portal')}
              </Button>
            ) : null}

            {canInviteInvestor ? (
              <Button
                variant='outlined'
                onClick={() => {
                  setInviteOpen(true);
                }}
              >
                {t('sendInvite', 'Send Invite')}
              </Button>
            ) : null}

            {client.CanMGPSSO && isMgpEnabled ? (
              <Button
                variant='outlined'
                onClick={handleAccessMoneyGuide}
              >
                {t('accessInMoneyGuide', 'Access in MoneyGuide')}
              </Button>
            ) : null}

            {/* Match Clients.tsx action column behavior for correct menu anchoring */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ActionsMenuCell
                onClick={(e) => {
                  openMenuForInvestor(client, e);
                }}
              />
              <MenuForInvestor
                investor={client}
                hideTrigger
              />
            </Box>
          </Box>

          <Grid
            container
            spacing={4}
          >
            {data.map(({ title, value }) => (
              <Grid
                size={{ xs: 12, sm: 4 }}
                key={title}
              >
                <Typography
                  fontWeight='bold'
                  variant='subtitle2'
                >
                  {title}
                </Typography>
                <Typography>{value}</Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ py: 2 }} />

          <Box sx={{ py: 2 }}>
            {isMedium && tabs.length > 0 ? (
              <Tabs
                value={selectedTabTrigger}
                aria-label={t(
                  'clientDetail.tabsLabel',
                  'Client detail sections',
                )}
                onChange={(e, v: string) => {
                  if (v === 'more') {
                    openMoreMenuFromTabs(e);
                  } else {
                    selectTab(v);
                  }
                }}
              >
                {visibleTabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition='end'
                    // MUI's Tab only calls Tabs.onChange when it isn't
                    // already the selected tab, so an already-selected More
                    // trigger (the current tab is overflowed) would
                    // otherwise be unable to reopen its menu on click.
                    onClick={
                      tab.key === 'more' ? openMoreMenuFromTabs : undefined
                    }
                  />
                ))}

                {moreTabs.length > 0 && (
                  <Menu
                    anchorEl={moreAnchorEl}
                    open={isMoreOpen}
                    onClose={closeMoreMenu}
                  >
                    {moreTabs.map((tab) => (
                      <MenuItem
                        key={tab.key}
                        onClick={() => {
                          selectTab(tab.key);
                          closeMoreMenu();
                        }}
                      >
                        {tab.label}
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </Tabs>
            ) : (
              <FormControl
                fullWidth={isMobile}
                size='small'
              >
                <Select
                  labelId='terms-tab-select-label'
                  id='terms-tab-select'
                  value={effectiveTabKey ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (typeof value === 'string') {
                      selectTab(value);
                    }
                  }}
                  sx={(theme) => tabSelectDropdownStyling(theme)}
                >
                  {tabs.map((tab) => (
                    <MenuItem
                      key={tab.key}
                      value={tab.key}
                    >
                      {tab.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {effectiveTabKey === 'balanceSheet' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.PersonalFinancialStatementPage}
                widgetMap={advisorWidgetMap}
                investorId={client.InvestorId}
                defaultExpandLevel='category'
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'accounts' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.AccountsPage}
                widgetMap={advisorWidgetMap}
                endDate={todayIso}
                gridHeight={520}
                investorId={client.InvestorId}
                onlyPrimaryInvestors={false}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'holdings' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.ClientHoldingsTab}
                widgetMap={advisorWidgetMap}
                endDate={todayIso}
                gridHeight={520}
                investorId={client.InvestorId}
                onlyPrimaryInvestors={false}
                advisorClientName={client.Name}
                showCusip={showCusip}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'gainsLosses' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.GainlossPage}
                widgetMap={advisorWidgetMap}
                investorId={client.InvestorId}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'investmentActivity' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.InvestmentActivity}
                widgetMap={advisorWidgetMap}
                investorId={client.InvestorId}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'cashFlow' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.CashflowPage}
                widgetMap={advisorWidgetMap}
                investorId={client.InvestorId}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'assetAllocation' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.AssetAllocationPage}
                widgetMap={advisorWidgetMap}
                asOfDate={new Date(todayIso)}
                investorId={client.InvestorId}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'documents' && (
            <Box>
              <WidgetLoader
                code={AdvisorWidgetCodes.DocumentVault}
                widgetMap={advisorWidgetMap}
                investorId={client.InvestorId}
                hideTitle
                container='flyout'
              />
            </Box>
          )}

          {effectiveTabKey === 'insights' && (
            <React.Suspense
              fallback={
                <Typography variant='body2'>
                  {t('loading', 'Loading...')}
                </Typography>
              }
            >
              <ContentContainer container='flyout'>
                <Insights
                  investorId={client.InvestorId}
                  lastLogin={
                    typeof client.LastLogin === 'string'
                      ? client.LastLogin
                      : undefined
                  }
                  lastInviteDate={
                    typeof client.LastInviteDate === 'string'
                      ? client.LastInviteDate
                      : undefined
                  }
                />
              </ContentContainer>
            </React.Suspense>
          )}

          {effectiveTabKey === 'portalActivity' && (
            <ContentContainer container='flyout'>
              <PortalActivity investorId={client.InvestorId} />
            </ContentContainer>
          )}

          <React.Suspense fallback={<LoadingSpinner />}>
            {inviteOpen ? (
              <ClientInviteModal
                open={inviteOpen}
                onClose={() => {
                  setInviteOpen(false);
                }}
                selectedCount={1}
                selectedInvestorIds={[client.InvestorId]}
                selectedUserIds={[client.UserId]}
              />
            ) : null}
          </React.Suspense>
          <Snackbar
            open={Boolean(impersonationError)}
            autoHideDuration={6000}
            onClose={clearImpersonationError}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={clearImpersonationError}
              severity='error'
              sx={{ width: '100%' }}
            >
              {impersonationError}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ClientContent);
